import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntityTypesSupportingRoutes } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import AllottedTime from "@app/component/input/CreateRoute/AllottedTime";
import EndingLocation from "@app/component/input/CreateRoute/EndingLocation";
import RouteName from "@app/component/input/CreateRoute/RouteName";
import StartDateTime from "@app/component/input/CreateRoute/StartDateTime";
import StartingLocation from "@app/component/input/CreateRoute/StartingLocation";
import VisitOrder, { VisitType } from "@app/component/input/CreateRoute/VisitOrder";
import { reverseGeocodeAddress } from "@app/store/location/actions";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getDefaultStartDate from "@app/util/route/getDefaultStartDate";

import { getEntityType, isCreatingRoute } from "../../store";
import { createRoute } from "../../store/actions";
import StepProps from "../../type/StepProps";
import Footer from "../Footer";
import Step from "../Step";

import styles from "./Finalize.module.scss";

interface Props extends StepProps {
  entityType: EntityTypesSupportingRoutes;
  loading?: boolean;
  onCreateRoute?: typeof createRoute.request;
  onReverseGeocodeAddress: typeof reverseGeocodeAddress;
}

const Finalize: React.FC<Props> = ({
  entityType,
  loading,
  onBack,
  onCreateRoute,
  onHide,
  onReverseGeocodeAddress,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [name, setName] = useState("");
  const [visitOrder, setVisitOrder] = useState<VisitType>("optimized");
  const [allottedTime, setAllottedTime] = useState<number>(900); // 15 minutes by default
  const [startTime, setStartTime] = useState<Date>(getDefaultStartDate());
  const [startAddress, setStartAddress] = useState("");
  const [endAddress, setEndAddress] = useState("");

  const isDuplicateName = false; // TODO: think about supporting this
  const isValidRoute = !isDuplicateName && visitOrder && allottedTime !== undefined;

  useEffect(() => {
    analytics.using(["Build routes from activities", "Open step"], {
      stepName: "Finalize",
    });
  }, [analytics]);

  const handleCreateClick = useCallback(() => {
    onCreateRoute?.({
      callback: onHide,
      route: {
        entityType,
        name,
        routeDetail: {
          allottedTime,
          endAddress,
          startAddress,
          startAt: startTime.toISOString(),
          type: visitOrder,
        },
      },
    });
    analytics.using(["Build routes from activities", "Create Route"]);
  }, [
    allottedTime,
    analytics,
    onCreateRoute,
    endAddress,
    entityType,
    name,
    startAddress,
    startTime,
    visitOrder,
    onHide,
  ]);

  return (
    <Step
      contentClassName={styles.container}
      footer={
        <Footer>
          <Button onClick={onBack} type="text">
            {intl.formatMessage({
              id: "activities.calendarView.routeBuilder.steps.finalize.footer.back",
              defaultMessage: "Back",
              description:
                "Back button in Finalize step in Route Builder in Activities Calendar view",
            })}
          </Button>
          <Button
            disabled={!isValidRoute || loading}
            loading={loading}
            onClick={handleCreateClick}
            type="primary"
          >
            {intl.formatMessage({
              id: "activities.calendarView.routeBuilder.steps.finalize.footer.next",
              defaultMessage: "Create Route",
              description:
                "Next button in Finalize step in Route Builder in Activities Calendar view",
            })}
          </Button>
        </Footer>
      }
    >
      <RouteName isDuplicateName={isDuplicateName} onChange={setName} value={name} />
      <VisitOrder onChange={setVisitOrder} value={visitOrder} />
      <StartDateTime onChange={setStartTime} value={startTime} />
      <AllottedTime onChange={setAllottedTime} value={allottedTime} />
      <StartingLocation
        onChange={setStartAddress}
        onReverseGeocodeAddress={reverseGeocodeAddress}
        value={startAddress}
      />
      <EndingLocation
        onChange={setEndAddress}
        onReverseGeocodeAddress={onReverseGeocodeAddress}
        value={endAddress}
      />
    </Step>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getEntityType(state),
  loading: isCreatingRoute(state),
});

const mapDispatchToProps = {
  onCreateRoute: createRoute.request,
  onReverseGeocodeAddress: reverseGeocodeAddress,
};

export default connect(mapStateToProps, mapDispatchToProps)(Finalize);
